import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { type CreateUserRequest, type User, usersApi } from '@/services/api';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: usersApi.getUsers,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function getUserDisplayName(user: User | undefined, userId: string | undefined, currentUserId?: string): string {
  if (!userId) {
    return '';
  }

  if (userId === currentUserId) {
    return 'You'; // This will be handled in components with translation
  }

  if (user?.name) {
    return user.name;
  }

  if (user?.email) {
    return user.email;
  }

  return userId;
}

export function useUserDisplayName() {
  const { t } = useTranslation();

  return (user: User | undefined, userId: string | undefined, currentUserId?: string): string => {
    if (!userId) {
      return '';
    }

    if (userId === currentUserId) {
      return t('common.you');
    }

    if (user?.name) {
      return user.name;
    }

    if (user?.email) {
      return user.email;
    }

    return userId;
  };
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequest) => usersApi.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
